import React, { useEffect, useState } from "react";
import { Form, Button, Card, ListGroup, Spinner } from "react-bootstrap";
import SignaturePad from "../predioPrograma/signaturePad";
import {CreateSubmissionDto, FormPayload, Predio, SubmissionNotes, User} from "../../../../service/types/types";
import { PrediosService } from "../../../../service/predios.service";
import { UserService } from "../../../../service/users.service";
import { SubmissionValue } from "../../../../service/types/types";
import Select, { MultiValue } from "react-select";
import { useParams } from "react-router-dom";
import { FormsService } from "../../../../service/forms.service";
import { FileService } from "../../../../service/file.service";
import {submitWithOfflineSupport} from "../../../../service/hook/useOfflineSubmission";
import {requestNotificationPermission} from "../../../../service/hook/useNotifications";

export default function DynamicFormRenderer() {
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [signature, setSignature] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [values, setValues] = useState<SubmissionValue[]>([]);
  const [predios, setPredios] = useState<Predio[]>([]);
  const [prediosOptions, setPrediosOptions] = useState<{ value: string; label: string; }[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [usersOptions, setUsersOptions] = useState<{ value: string; label: string; }[]>([]);
  const { id } = useParams<{ id: string }>();
  const [files, setFiles] = useState<File[]>([]);
  const [fileNameToPath, setFileNameToPath] = useState<Map<string, string>>(new Map());
  const [fileNameToFieldId, setFileNameToFieldId] = useState<Map<string, string>>(new Map());
  const [form, setForm] = useState<FormPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState<SubmissionNotes[]>([]);
  const [showNotesForm, setShowNotesForm] = useState(false);
  const [noteText, setNoteText] = useState<string>("");
  const [noteLocation, setNoteLocation] =useState<{ lat: number; lng: number } | null>(null);
  const [noteImage, setNoteImage] = useState<File | null>(null);
  useEffect(() => {
    const fetchForm = async () => {
      if (!id) return;
      const data = await FormsService.findById(id);
      setForm(data);
      setLoading(false);
      if(data?.fields?.some((field) => field.type === "trabajadores")){
        const fetchUsers = async () => {
          try {
            const data = await UserService.findAll();
            setUsers(data);
            const options = data.map(user => ({
              value: user.rut,              
              label: `${user.name} (${user.turno})`,
            }));
            setUsersOptions(options);
          } catch (error) {
            console.error('Error fetching users:', error);
          }
        };
        fetchUsers();
      }
      if(data?.fields?.some((field) => field.type === "predios")){
        const fetchPredios = async () => {
          try {
            const data = await PrediosService.findAll();
            setPredios(data);
            const options = data.map(predio => ({
              value: predio.id,              
              label: `${predio.name}`,
            }));
            setPrediosOptions(options);
          } catch (error) {
            console.error('Error fetching users:', error);
          }
        };
        fetchPredios(); 
      }
    };
    fetchForm();
  }, [id]);

  useEffect(() => {
    const valuesMap: Map<string, string> = new Map();

    fileNameToFieldId.forEach((fieldId, fileName) => {
      const path = fileNameToPath.get(fileName);
      if (path) {
        const existing = valuesMap.get(fieldId);
        if (existing) {
          // si ya existe, concatenamos el nuevo path
          valuesMap.set(fieldId, existing + ", " + path);
        } else {
          valuesMap.set(fieldId, path);
        }
      }
    });

    // Convertir el Map a array de SubmissionValue
    const submissionValues: SubmissionValue[] = Array.from(valuesMap.entries()).map(
      ([fieldId, value]) => ({ fieldId, value })
    );
    // Fusionar con el estado anterior
    setValues(prev => {
      const merged = new Map(prev.map(v => [v.fieldId, v.value]));

      submissionValues.forEach(({ fieldId, value }) => {
        merged.set(fieldId, value); // override si existe
      });

      return Array.from(merged.entries()).map(([fieldId, value]) => ({ fieldId, value }));
    });
  }, [fileNameToPath, fileNameToFieldId]);

  const handleDrawing = (fieldId: number, value: any) => {
    setSignature(value);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    if (isMobile || isPWA) {
      console.log('Mobile-request');
      await requestNotificationPermission();
    }
    if(id){
      const submission:  CreateSubmissionDto = {
        signature: signature,
        values: values,
        notes: notes,
        rut: localStorage.getItem("rut") || undefined,
      };
      try {
        setLoading(true);
        const { online } = await submitWithOfflineSupport(id, submission);
        console.log(online);
        if (!online) {
          alert('Sin conexión. El formulario se enviará cuando vuelva la red, NO REENVIE.');
        } else {
          alert("Formulario enviado correctamente");
        }

        setLoading(false);
        window.location.reload();
      } catch (error) {
        console.error('Error submitting form:', error);
        setLoading(false);
        alert("Error enviando formulario: " + (error as any)?.message || "Error desconocido");
      }
    }
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, fieldId: string) => {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    // Solo tomar el primer archivo
    const file = input.files[0];

    // Remover archivo anterior del mismo fieldId si existe
    const previousFileName = Array.from(fileNameToFieldId.entries()).find(([_, fId]) => fId === fieldId)?.[0];
    if (previousFileName) {
      await handleRemoveFile(files.findIndex(f => f.name === previousFileName), files.find(f => f.name === previousFileName) as File);
    }

    setFiles((prev) => [...prev, file]);
    uploadFile(file, fieldId);
  };

  const uploadFile = async (file: File, fieldId: string) => {
    const formDataUpload = new FormData();
    formDataUpload.append("files", file);
    try {
      setLoading(true);
      const response = await FileService.upload(formDataUpload);
      setFileNameToPath(prev => {
        const newMap = new Map(prev);
        newMap.set(file.name, response.paths[0]);
        return newMap;
      });
      setFileNameToFieldId(prev => {
        const newMap = new Map(prev);
        newMap.set(file.name, fieldId);
        return newMap;
      });
    } catch (err) {
      alert("Error subiendo archivo: " + (err as any)?.message || "Error desconocido");
    } finally {
      setLoading(false);
    }
  }

  const handleRemoveFile = async (index: number, file: File) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    try {
      let pathFile = fileNameToPath.get(file.name);
      pathFile = pathFile?.split("/").pop() || ""; // extraer solo el nombre del archivo
      const response = await FileService.remove(pathFile);
    } catch (err) {
      alert("Error eliminando archivo");
    }
    setFileNameToPath(prev => {
      const newMap = new Map(prev);
      newMap.delete(file.name);
      return newMap;
    });
    setFileNameToFieldId(prev => {
      const newMap = new Map(prev);
      newMap.delete(file.name);
      return newMap;
    });

  };

  const handleGetLocation = (fieldId: string) => {
    if (!navigator.geolocation) {
      alert("La geolocalización no está soportada en este navegador.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const cord = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }
        setCoords(cord);
        handleChange(fieldId, JSON.stringify(cord));
        setError(null);
      },
      (err) => {
        alert("No se pudo obtener la ubicación: " + err.message);
      }
    );
  };
  const handleChange = (fieldId: string, value: any) => {
    setValues((prevValues) => {
      const existingIndex = prevValues.findIndex((v) => v.fieldId === fieldId);

      if (existingIndex !== -1) {
        // Ya existe → reemplazar el valor
        const updatedValues = [...prevValues];
        updatedValues[existingIndex] = { fieldId, value };
        return updatedValues;
      } else {
        // No existe → agregar nuevo
        return [...prevValues, { fieldId, value }];
      }
    });
  };
  const handleSelect = (fieldId: string, options: MultiValue<{ value: string; label: string }>) => {
    const value = options.map(opt => opt.label).join(", ");
     setValues((prevValues) => {
      const existingIndex = prevValues.findIndex((v) => v.fieldId === fieldId);

      if (existingIndex !== -1) {
        // Ya existe → reemplazar el valor
        const updatedValues = [...prevValues];
        updatedValues[existingIndex] = { fieldId, value };
        return updatedValues;
      } else {
        // No existe → agregar nuevo
        return [...prevValues, { fieldId, value }];
      }
    });
  };

  const handleNoteImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    setNoteImage(input.files[0]);
  };

  const uploadNoteImage = async (file: File): Promise<string> => {
    const formDataUpload = new FormData();
    formDataUpload.append("files", file);
    try {
      const response = await FileService.upload(formDataUpload);
      return response.paths[0];
    } catch (err) {
      alert("Error subiendo imagen de nota: " + (err as any)?.message || "Error desconocido");
      throw err;
    }
  };
  const fileToBase64 = (file: File): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });

  const handleAddNote = async () => {
    if (!noteText.trim()) {
      alert("Por favor ingresa un texto para la nota");
      return;
    }

    try {
      setLoading(true);
      let imageUrl = "";
      let location = "";
      let imageBase64 = "";

      if (noteImage) {
        imageBase64 = await fileToBase64(noteImage);
      }
      if (noteLocation) {
        location = JSON.stringify(noteLocation);
      }

      const newNote: SubmissionNotes = {
        noteText,
        imageBase64,
        imageUrl,
        location: location
      };

      setNotes((prevNotes) => [...prevNotes, newNote]);

      // Limpiar formulario
      setNoteText("");
      setNoteImage(null);
      setShowNotesForm(false);

      alert("Nota añadida correctamente");
    } catch (err) {
      console.error('Error al agregar nota:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNoteLocation = () => {
    if (!navigator.geolocation) {
      alert("La geolocalización no está soportada en este navegador.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
        (position) => {
          const cord = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setNoteLocation(cord);
        },
        (err) => {
          const messages: Record<number, string> = {
            1: "Permiso denegado por el usuario, habilite el permiso.",
            2: "Posición no disponible.",
            3: "Tiempo de espera agotado, re intente mas tarde.",
          };
          alert("No se pudo obtener la ubicación: " + (messages[err.code] || err.message));
        },
        {
          enableHighAccuracy: true,  // force GPS, not cached cell/wifi
          timeout: 10000,            // fail after 10s instead of hanging
          maximumAge: 0,             // never use a cached position
        }
    );
  };
  const handleRemoveNote = (index: number) => {
    setNotes((prevNotes) => prevNotes.filter((_, i) => i !== index));
  };

  return (
  <div className="w-100 p-4">
  <h2 className="mb-4">{form?.name}</h2>
  <Form onSubmit={handleSubmit} className="w-100">
    {form?.fields.map(field => (
      <Form.Group key={field.id} className="mb-3 w-100">
        <Form.Label>
          {field.label} {field.required && "*"}
        </Form.Label>

        {field.type === "text" && (
          <Form.Control
            type="text"
            required={field.required}
            className="w-100"
            onChange={e => handleChange(field.id, e.target.value)}
          />
        )}

        {field.type === "number" && (
          <Form.Control
            type="number"
            required={field.required}
            className="w-100"
            onChange={e => handleChange(field.id, e.target.value)}
          />
        )}

        {field.type === "date" && (
          <Form.Control
            type="datetime-local"
            required={field.required}
            className="w-100"
            onChange={e => handleChange(field.id, e.target.value)}
          />
        )}

        {field.type === "files" && (
          <Form.Control
            type="file"
            accept="image/*"        // acepta imágenes
            onChange={(e) => handleFileChange(e, field.id)}
          />
        )}

        {field.type === "select" && (
          <Form.Select
            required={field.required}
            className="w-100"
            onChange={e => handleChange(field.id, e.target.value)}
          >
            <option value="">Seleccione...</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Form.Select>
        )}

        {field.type === "trabajadores" && (
          <Select
            options={usersOptions}
            onChange={(options) => handleSelect(field.id, options)}
            className="w-100"
            isMulti
            placeholder="Escribe para buscar..."
            isSearchable
          />
        )}

        {field.type === "predios" && (
          <Select
            options={prediosOptions}
            onChange={(options) => handleSelect(field.id, options)}
            isMulti
            className="w-100"
            placeholder="Escribe para buscar..."
            isSearchable
          />
        )}
        {field.type === "location" && (
          <div className="d-flex">
          <Form.Control
            type="text"
            readOnly
            value={
              coords
                ? `Lat: ${coords.lat}, Lng: ${coords.lng}`
                : "Presiona el botón para obtener ubicación"
            }
          />
          <Button
            variant="primary"
            className="ms-2"
            onClick={e => handleGetLocation(field.id)}
          >
            Obtener
          </Button>
        </div>
        )}
      </Form.Group>
    ))}

    {/* Sección de Notas */}
    <div className="mb-4">
      {showNotesForm ? (
          <Button
            variant="outline-secondary"
            onClick={() => setShowNotesForm(!showNotesForm)}
            className="mb-3 w-100"
          >
            Ocultar Formulario de Notas
          </Button>):
          (<Button
            variant="outline-success"
            onClick={() => setShowNotesForm(!showNotesForm)}
            className="mb-3 w-100"
          >
            + Añadir Nota
          </Button>)
      }
      {showNotesForm && (
        <Card className="mb-3 p-3">
          <Form.Group className="mb-3">
            <Form.Control
              as="textarea"
              rows={3}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Escribe tu nota aquí..."
              className="w-100"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <div className="d-flex">
              <Form.Control
                  type="text"
                  readOnly
                  value={
                    noteLocation
                        ? `Lat: ${noteLocation.lat}, Lng: ${noteLocation.lng}`
                        : "Presiona el botón para obtener ubicación (Opcional)"
                  }
              />
              <Button
                  variant="primary"
                  className="ms-2"
                  onClick={e => handleNoteLocation()}
              >
                Obtener
              </Button>
            </div>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleNoteImageChange}
              className="w-100"
            />
            {noteImage && (
              <div className="mt-2">
                <small className="text-muted">Archivo seleccionado: {noteImage.name}</small>
              </div>
            )}
          </Form.Group>

          <Button
            variant="success"
            onClick={handleAddNote}
            disabled={loading}
            className="w-100"
          >
            {loading ? "Guardando..." : "Guardar Nota"}
          </Button>
        </Card>
      )}

      {notes.length > 0 && (
        <div className="mb-4">
          <h5>Notas Agregadas ({notes.length})</h5>
          <ListGroup>
            {notes.map((note, index) => (
              <ListGroup.Item key={index} className="mb-2">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <p className="mb-2"><strong>Nota {index + 1}:</strong></p>
                    <p className="mb-2">{note.noteText}</p>
                    {note.imageUrl && (
                      <div className="mb-2">
                        <img
                          src={"/api" + note.imageUrl}
                          alt={`Nota ${index + 1}`}
                          style={{ maxWidth: "200px", maxHeight: "200px", borderRadius: "4px" }}
                        />
                      </div>
                    )}
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveNote(index)}
                  >
                    Eliminar
                  </Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
    {/* Campo de firma/dibujo */}
    { form?.isSignRequired && 
      <Form.Group className="mb-3 w-100">
        <Form.Label>Dibujo / Firma</Form.Label>
        <SignaturePad onChange={dataUrl => handleDrawing(-1, dataUrl)} />
      </Form.Group>
    }

    <Button variant="primary" type="submit" className="w-100">
      Enviar
    </Button>
  </Form>
    {/* Overlay cuando loading es true */}
      {loading && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            backgroundColor: 'rgba(255,255,255,0.7)', // fondo más claro
            zIndex: 9999,
          }}
        >
          <Spinner animation="border" role="status" />
          {/* O puedes usar una imagen personalizada */}
          {/* <img src="/loading.gif" alt="Cargando..." /> */}
        </div>
      )}
</div>
  );
}